import { Suspense } from "react";
import { ErrorBoundary, type FallbackProps } from "react-error-boundary";
import { reportError } from "@/app/_utils/reportError";
import { Outlet, useLocation } from "react-router-dom";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Link from "@mui/material/Link";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import { CircleAlert as ErrorOutlineIcon } from "lucide-react";

function AdminOutletFallback() {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        minHeight: 320,
        width: "100%",
        flex: 1,
      }}
    >
      <CircularProgress size={32} />
    </Box>
  );
}

function AdminErrorFallback({ error, resetErrorBoundary }: FallbackProps) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "calc(100vh - 120px)",
        width: "100%",
        flex: 1,
        p: 3,
      }}
    >
      <Paper
        elevation={0}
        sx={{
          maxWidth: 480,
          width: "100%",
          p: 4,
          borderRadius: 2,
          bgcolor: "#fff",
          border: "1px solid #e9e9e9",
          textAlign: "center",
        }}
      >
        <ErrorOutlineIcon
          style={{ width: "48.0px", height: "48.0px", color: "error.main", marginBottom: "16px" }}
          aria-hidden
        />
        <Typography variant="h6" sx={{ mb: 1.5, fontWeight: 600 }}>
          Something went wrong
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ wordBreak: "break-word", mb: 3 }}
        >
          {error.message}
        </Typography>
        <Box sx={{ display: "flex", justifyContent: "center", gap: 2, flexWrap: "wrap" }}>
          <Button variant="outlined" onClick={resetErrorBoundary}>
            Try again
          </Button>
          <Button component={Link} href="/admin" variant="text">
            Admin home
          </Button>
        </Box>
      </Paper>
    </Box>
  );
}

export default function AdminRouteOutlet() {
  const location = useLocation();
  return (
    <ErrorBoundary
      FallbackComponent={AdminErrorFallback}
      resetKeys={[location.pathname]}
      onError={(error) => reportError(error, "admin")}
    >
      <Suspense fallback={<AdminOutletFallback />}>
        <Outlet />
      </Suspense>
    </ErrorBoundary>
  );
}
